import {
  Flex,
  FormControl,
  FormErrorMessage,
  List,
  ListItem,
  Box,
  Image,
  Spinner,
} from "@chakra-ui/react";

import {
  AuthFormContainer,
  AuthCardEllipse,
  AuthCardTopFrame,
  KpecLogoText,
  AuthHeading,
  AuthSubheading,
  AuthFieldsWrapper,
  AuthInputBox,
  AuthInputField,
  AuthLabelText,
  AuthButton,
  AuthButtonWrapper,
} from "@root/commonStyles/commonStyle";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setNewPassword, verifyResetPass } from "@root/store/thunks";
import { ROUTE_NAMES } from "@root/constants";
import { useOrganizationBranding } from "@root/context/organization-branding-context";

interface ResetPasswordValues {
  password: string;
  rePassword: string;
}
interface DisplayRequirementsProps {
  requirements: string;
}

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation("common");
  const {
    logoUrl,
    defaultLogoUrl,
    loading: brandingLoading,
  } = useOrganizationBranding();
  const initialValues: ResetPasswordValues = {
    password: "",
    rePassword: "",
  };

  const location = useLocation();
  const { resetToken, email } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  console.log("isVerified", isVerified);
  const dispatch = useDispatch();

  const handleSucess = () => {
    setIsVerified(true);
  };

  // useEffect(() => {
  //   if (resetToken && !isVerified) {
  //     dispatch(
  //       verifyResetPass({
  //         payload: resetToken,
  //         onSuccess: () => handleSucess(),
  //       })
  //     );
  //   }
  // }, [resetToken, isVerified]);

  const validate = (values: ResetPasswordValues) => {
    const errors: Partial<ResetPasswordValues> = {};
    if (!values.password) {
      errors.password = t("login.requiredPassword");
    } else if (values.password.length < 6) {
      errors.password = t("login.passwordValidation");
    }

    if (!values.rePassword) {
      errors.rePassword = t("resetPassword.confirmRequiredPassword");
    } else if (values.rePassword !== values.password) {
      errors.rePassword = t("resetPassword.samePasword");
    }

    return errors;
  };

  const navigate = useNavigate();
  const onSuccess = () => {
    setLoading(false);
    navigate(ROUTE_NAMES.RESET_SUCCESS);
  };
  // TODO: Replace 'user@example.com' with the actual email source
  const onSubmitHandler = (values: ResetPasswordValues) => {
    setLoading(true);
    if (!email || !resetToken) {
      setLoading(false);
      alert(
        "Missing reset token or email. Please restart the password reset process."
      );
      return;
    }
    dispatch(
      setNewPassword({
        email,
        newPassword: values.password.trim(),
        confirmPassword: values.rePassword,
        resetToken,
        onSuccess,
        onError: () => setLoading(false),
      })
    );
  };

  const DisplayRequirements: React.FC<DisplayRequirementsProps> = ({
    requirements,
  }) => {
    const lines = requirements.split("\n");

    return (
      <Box
        fontSize="14px"
        alignItems="flex-start"
        fontWeight="400"
        color={"theme.gray.500"}
      >
        Requirements
        <Box
          fontWeight="400"
          fontSize="14px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          color="gray.500"
        >
          <List spacing={1} alignItems="flex-start">
            {lines.map((line, index) => {
              if (line.startsWith("• ")) {
                return <ListItem key={index}>{line.trim()}</ListItem>;
              } else if (line.startsWith("  • ")) {
                return <ListItem key={index}>{line.trim()}</ListItem>;
              } else {
                return null;
              }
            })}
          </List>
        </Box>
      </Box>
    );
  };

  return (
    <AuthFormContainer>
      <AuthCardEllipse />
      <AuthCardTopFrame>
        <Flex align="center" gap="12.8px" minH="38px">
          {brandingLoading ? (
            <Spinner size="md" />
          ) : (
            <Image
              src={logoUrl || defaultLogoUrl}
              alt="logo"
              maxH="38px"
              objectFit="contain"
            />
          )}
        </Flex>
        <Flex direction="column" align="stretch" width="100%" gap="10px">
          <AuthHeading>
            {t("resetPassword.subTitle", "Reset Password")}
          </AuthHeading>
          <AuthSubheading>{t("resetPassword.subtitle_1")}</AuthSubheading>
        </Flex>
      </AuthCardTopFrame>

      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={onSubmitHandler}
      >
        {({ handleSubmit, errors, touched, values, handleChange }) => (
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <AuthFieldsWrapper>
              <Flex
                direction="column"
                align="flex-start"
                gap="1rem"
                width="100%"
              >
                <Flex
                  direction="column"
                  align="flex-start"
                  gap="0.5rem"
                  width="100%"
                >
                  <AuthLabelText>
                    {t("resetPassword.newPassword")}
                  </AuthLabelText>
                  <FormControl
                    isInvalid={!!errors.password && touched.password}
                    width="100%"
                  >
                    <AuthInputBox>
                      <AuthInputField
                        id="password"
                        name="password"
                        type="password"
                        required
                        placeholder={t("resetPassword.password")}
                        value={values.password}
                        onChange={handleChange}
                      />
                    </AuthInputBox>
                    <FormErrorMessage>{errors.password}</FormErrorMessage>
                  </FormControl>
                </Flex>
                <Flex
                  direction="column"
                  align="flex-start"
                  gap="0.5rem"
                  width="100%"
                >
                  <AuthLabelText>
                    {t("resetPassword.confirmPassword")}
                  </AuthLabelText>
                  <FormControl
                    isInvalid={!!errors.rePassword && touched.rePassword}
                    width="100%"
                  >
                    <AuthInputBox>
                      <AuthInputField
                        id="rePassword"
                        name="rePassword"
                        type="password"
                        required
                        placeholder={t("resetPassword.confirmPassword")}
                        value={values.rePassword}
                        onChange={handleChange}
                      />
                    </AuthInputBox>
                    <FormErrorMessage>{errors.rePassword}</FormErrorMessage>
                  </FormControl>
                </Flex>
              </Flex>
            </AuthFieldsWrapper>

            <AuthButtonWrapper>
              <AuthButton type="submit" isLoading={loading}>
                {t("forgetPassword.continue")}
              </AuthButton>
            </AuthButtonWrapper>
          </form>
        )}
      </Formik>
    </AuthFormContainer>
  );
};
